<template>
  <div id="viewer" ref="viewer" :class="{ viewer: true, crosshair: lineMode }" @contextmenu.prevent>
    <ShortcutsDialog
      @onEscapePressed="handleCancelAnnotationMode"
      @onDeletePressed="handleDeleteLineMarkers"
      @onOrientationSelected="handleBearingAndElevationUpdate"
    />
  </div>
</template>

<script>
import { get } from 'lodash';
import 'photo-sphere-viewer/dist/photo-sphere-viewer.css';
import 'photo-sphere-viewer/dist/plugins/markers.css';

import { Viewer } from 'photo-sphere-viewer';
import MarkersPlugin from 'photo-sphere-viewer/dist/plugins/markers';
import MeasurementController from '@/controllers/MeasurementController';
import { deleteQueries } from '@/utils/queryActions';
import utils, { Orientation } from '@/utils';
import { formatDistanceMeasurement } from '@/utils/measure';
import { notificationType } from '@/components/widgets';
import { ShortcutsDialog } from './components';

export default {
  name: 'PanoViewer',
  components: {
    ShortcutsDialog,
  },
  props: {
    source: undefined,
    initial: {
      type: Object,
      default: () => ({
        bearing: 0,
        elevation: 0,
        zoom: 0,
        markerId: '',
      }),
    },
    markers: { type: Array, default: () => [] },
    bearing: { type: Number, default: 0 },
    elevation: { type: Number, default: 0 },
    zoom: { type: Number, default: 0 },
    neighbouringImages: { type: Array, default: () => [] },
    line: { type: Object, default: () => ({ points: [], measurements: [] }) },
    checkedLines: { type: Array, default: () => [] },
    inspectionConfig: { type: Object, default: () => ({}) },
    displaySnackbar: { type: Function, default: () => {} },
  },
  data() {
    return {
      lineMode: false,
      selectedLines: new Set(),
      lineStart: [],
      lineEnd: [],
      viewer: undefined,
      markersPlugin: undefined,
      loaded: false,
      ready: false,
      position: {
        longitude: 0,
        latitude: 0,
      },
    };
  },
  watch: {
    ready(now) {
      if (now) this.updateMarkers(this.markers);
    },
    lineMode(now, old) {
      if (now && now !== old) {
        this.displaySnackbar('Line Mode enabled');
      }
    },
    source(now) {
      this.loaded = false;
      this.viewer.setPanorama(now).then(() => {
        this.loaded = true;
      });
    },

    neighbouringImages() {
      this.$emit('marker-updated');
    },

    markers: {
      deep: true,
      handler(now) {
        this.updateMarkers(now);
      },
    },

    checkedLines() {
      this.$emit('marker-updated');
    },

    initial: {
      deep: true,
      handler(now, old) {
        if (old.bearing || old.elevation) {
          this.updateBearingElevation(now);
        }
      },
    },
  },

  destroyed() {
    try {
      this.viewer.destroy();
    } catch (error) {
      console.log(`error destroying pano viewer: ${error.message}`);
    } finally {
      this.ready = false;
      // delete queries
      deleteQueries(['bearing', 'elevation', 'zoom']);
    }
  },
  mounted() {
    this.viewer = new Viewer({
      plugins: [[MarkersPlugin, { markers: this.markers, clickEventOnMarker: true }]],
      container: 'viewer',
      panorama: this.source,
      maxFov: 100,
      minFov: 2,
      defaultZoomLvl: 2,
      autorotateSpeed: '10rpm',
      autorotateDelay: false,
      navbar: ['autorotate', 'zoom', 'fullscreen', this.getMeasureNavbar()],
    });

    this.markersPlugin = this.viewer.getPlugin(MarkersPlugin);

    this.markersPlugin.on('select-marker', (e, marker) => {
      if (!this.lineMode) this.$emit('marker-selected', marker);
    });

    this.markersPlugin.on('unselect-marker', () => {
      this.$emit('marker-unselected');
    });

    this.viewer.once('ready', () => {
      this.ready = true;
      this.viewer.rotate({
        latitude: Number(this.initial.elevation),
        longitude: Number(this.initial.bearing),
      });
      this.viewer.zoom(Number(this.initial.zoom));
      if (this.initial.equipmentId) {
        const marker = this.markers.find(({ equipmentId }) => equipmentId === this.initial.equipmentId);
        if (marker) {
          this.markersPlugin.trigger('select-marker', marker);
          if (!this.lineMode) this.$emit('marker-selected', marker);
        }
      }
    });

    this.viewer.on('click', this.onClick);

    this.viewer.on('position-updated', this.onPositionUpdate);

    this.viewer.on('zoom-updated', this.onZoomUpdate);
  },

  methods: {
    // update URL queries for bearing,elevation,zoom
    render() {
      if (this.viewer.renderer.camera != null) {
        try {
          this.viewer.needsUpdate();
        } catch (error) {
          console.log('render error', error);
        }
      } else console.log('not ready');
    },

    addMarker(elem) {
      try {
        this.markersPlugin.addMarker(elem, false);
        return true;
      } catch (error) {
        console.log(error);
        return false;
      }
    },

    removeMarker(id) {
      try {
        this.markersPlugin.removeMarker(id);
        return true;
      } catch (error) {
        return false;
      }
    },

    addMetaMarkers() {
      this.getNeighbourMarkers().forEach((element) => {
        this.addMarker(element);
      });

      this.getLineMarkers().forEach((element) => {
        this.addMarker(element);
      });

      // add current annotation ends
      if (this.lineStart.length) {
        this.addMarker({
          id: 'linestart_0',
          circle: 6,
          longitude: this.lineStart[0],
          latitude: this.lineStart[1],
          style: {
            cursor: 'pointer',
          },
          svgStyle: {
            stroke: 'rgb(0,255,0)',
            fill: 'rgba(0,0,0,.5)',
          },
        });
      }
      if (this.lineEnd.length) {
        this.addMarker({
          id: 'linestart_1',
          circle: 6,
          longitude: this.lineEnd[0],
          latitude: this.lineEnd[1],
          style: {
            cursor: 'pointer',
          },
          svgStyle: {
            stroke: 'rgb(0,255,0)',
            fill: 'rgba(0,0,0,.5)',
          },
        });
      }
    },

    updateMarkers(now) {
      if (!this.ready) return;
      this.markersPlugin.clearMarkers();
      // TODO: now contains duplicate polygon markers causing console errors, fix in future
      now.forEach((element) => {
        this.addMarker(element);
      });

      this.addMetaMarkers();

      this.render();
    },

    deleteLineMarkers() {
      this.line.points = this.line.points.filter((i, idx) => !this.selectedLines.has(idx));
      this.line.measurements = this.line.measurements.filter((i, idx) => !this.selectedLines.has(idx));
      this.$emit('lines-updated', this.line);
      this.selectedLines.clear();
    },

    selectLineMarker(id) {
      if (!id || Number.isNaN(id)) return;
      let ret;
      if (!this.selectedLines.has(Number(id))) {
        this.selectedLines.add(Number(id));
        ret = true;
      } else {
        this.selectedLines.delete(Number(id));
        ret = false;
      }
      this.$emit('marker-updated');
      return ret;
    },

    getLineTooltip(idx) {
      return `Click to ${
        this.selectedLines.has(idx) ? 'unselect' : 'select'
      }<br /><b>Measurement:</b><br />${formatDistanceMeasurement({
        meters: this.line.measurements[idx],
        unit: this.$store.state.unit,
        displayUnit: true,
      })}`;
    },

    // markers for line annotations
    getLineMarkers() {
      // add line markers
      const checkedMarkers = new Set(this.checkedLines);
      return this.line.points
        .filter((line, idx) => checkedMarkers.has(`line_${idx}`))
        .reduce(
          (acc, line, idx) =>
            acc.concat(
              // add line marker
              [
                {
                  id: `line_${idx}`,
                  polylineRad: line,
                  tooltip: this.getLineTooltip(idx),
                  style: {
                    cursor: 'pointer',
                  },
                  svgStyle: {
                    stroke: this.selectedLines.has(idx) ? 'rgb(0,0,255)' : 'rgb(255,0,0)',
                    strokeWidth: '4px',
                  },
                },
              ],
              // add enpoint markers
              line.map((lineend, iidx) => ({
                id: `lineend_${idx}_${iidx}`,
                circle: 6,
                longitude: lineend[0],
                latitude: lineend[1],
                style: {
                  cursor: 'pointer',
                },
                tooltip: this.getLineTooltip(idx),
                svgStyle: {
                  stroke: this.selectedLines.has(idx) ? 'rgb(255,0,255)' : 'rgb(0,255,0)',
                  fill: 'rgba(0,0,0,.5)',
                },
              }))
            ),
          []
        );
    },

    // markers for neighbouring images
    getNeighbourMarkers() {
      // add new neighbour markers
      const markerSize = 100;
      return this.neighbouringImages.map(({ spherical, image_id, image_name, atHeights }) => {
        // linear shrinking based on range
        const range = spherical.range < 1 ? 1 : spherical.range || 1;
        let nMarkerSize = markerSize / range;
        // cutoff at 20
        nMarkerSize = Math.max(nMarkerSize, 20);
        return {
          id: `neighbour_${image_id}`,
          image: `${window.location.origin}/${atHeights ? 'marker-at-height' : 'marker'}.png`,
          width: nMarkerSize,
          height: nMarkerSize,
          tooltip: `${
            image_name ? `<b>Location:</b> ${utils.formatImageNameForDisplay(image_name)}<br>` : ''
          }<b>Distance:</b> ${formatDistanceMeasurement({
            meters: spherical.range,
            unit: this.$store.state.unit,
            displayUnit: true,
          })}`,
          longitude: spherical.bearing,
          latitude: -spherical.elevation,
        };
      });
    },

    async endAnnotation(longitude, latitude) {
      this.lineEnd = [longitude, latitude];
      this.$emit('marker-updated');
      const line = [this.lineStart, this.lineEnd];
      this.line.points.push(line);
      this.lineMode = false;
      try {
        const measurement = await MeasurementController.getMeasurement(this.source, this.$route.query.image, line);
        this.line.measurements.push(measurement.data.line.distance[0][0]);
      } catch (error) {
        this.line.measurements.push(0);
        this.displaySnackbar(get(error, 'response.data.message', error.message), notificationType.error);
      }
      // clear line start/end
      this.lineStart = [];
      this.lineEnd = [];
      this.$emit('lines-updated', this.line);
      this.$emit('marker-updated');
    },

    // start/end line annotation
    startAnnotation(longitude, latitude) {
      if (!this.lineStart.length) {
        this.lineStart = [longitude, latitude];
        this.$emit('marker-updated');
        this.displaySnackbar('Line annotation started (ESC to cancel)');
      } else {
        this.endAnnotation(longitude, latitude);
      }
    },

    // cancel line annotation
    cancelAnnotation() {
      if (!this.lineStart.length) return;
      this.lineStart = [];
      this.$emit('marker-updated');
      this.displaySnackbar('Line annotation cancelled');
    },

    clearAnnotations() {
      this.lineStart = [];
      this.line.points = [];
      this.line.measurements = [];
      this.$emit('lines-updated', this.line);
      this.selectedLines.clear();
    },

    // click handler for photo-sphere-viewer
    onClick(e, data) {
      if (!this.lineMode) return;
      // mark line start
      if (!this.lineStart.length) {
        this.startAnnotation(data.longitude, data.latitude);
      } else {
        this.endAnnotation(data.longitude, data.latitude);
      }
    },

    updateBearingElevation({ bearing, elevation }) {
      this.viewer.animate({
        longitude: bearing,
        latitude: elevation,
        zoom: this.zoom,
        speed: '-10rpm',
      });
    },

    onPositionUpdate(e, position) {
      this.position = position;
      const zoom = this.viewer.getZoomLevel();
      this.$emit('position', { ...this.position, zoom });
    },

    onZoomUpdate(e, zoom) {
      this.$emit('position', { ...this.position, zoom });
    },

    getMeasureNavbar() {
      if (this.inspectionConfig.platformFeatures.hasMeasurementData) {
        return {
          id: 'line-button',
          title: 'Measure Line',
          className: 'ruler',
          content:
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M8 14h-8v-2h8.672l-.672 2zm-8 10v-8h24v8h-24zm2-2h20v-4h-2v2h-1v-2h-2v3h-1v-3h-2v2h-1v-2h-2v2h-1v-2h-2v3h-1v-3h-2v2h-1v-2h-2v4zm9.398-12.26l-1.398 4.26 4.227-1.432-2.829-2.828zm9.774-9.74l2.828 2.828-8.587 8.554-2.828-2.828 8.587-8.554z"/></svg>',
          onClick: () => {
            this.lineMode = !this.lineMode;
          },
        };
      }
      return {};
    },

    mapToBearingAndElevation(orientation) {
      switch (orientation) {
        case Orientation.DOWN:
          return { bearing: 0, elevation: -Math.PI + 0.01 };
        case Orientation.BACK:
          return { bearing: Math.PI, elevation: 0 };
        case Orientation.LEFT:
          return { bearing: -Math.PI / 2, elevation: 0 };
        case Orientation.UP:
          return { bearing: 0, elevation: Math.PI - 0.01 };
        case Orientation.RIGHT:
          return { bearing: Math.PI / 2, elevation: 0 };
        case Orientation.CENTER:
          return { bearing: 0, elevation: 0 };
        default:
          throw Error(`Unexpected numpad orientation key: ${orientation}`);
      }
    },

    // Todo: Change this Super Hack
    checkActivedModal() {
      const modalActiveClasses = ['.v-dialog--active', '.userback-overlay', '.v-input--is-focused'];
      return document.querySelector(modalActiveClasses);
    },

    handleBearingAndElevationUpdate({ active, orientation }) {
      if (!this.checkActivedModal() || active) {
        this.updateBearingElevation(this.mapToBearingAndElevation(orientation));
      }
    },

    handleCancelAnnotationMode(active) {
      if (!this.checkActivedModal() || active) {
        this.cancelAnnotation();
      }
    },

    handleDeleteLineMarkers(active) {
      if (!this.checkActivedModal() || active) {
        this.deleteLineMarkers();
      }
    },
  },
};
</script>

<style>
.ruler:hover {
  -webkit-transform: scale(1.2);
  transform: scale(1.2);
  transition: transform 200ms ease, -webkit-transform 200ms ease;
}
.ruler {
  transition: transform 200ms ease, -webkit-transform 200ms ease;
  fill: rgba(255, 255, 255, 0.7);
}
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.viewer {
  width: 100%;
  height: 90%;
  padding-top: 12px;
}

.viewer:first-child {
  position: sticky !important;
}
</style>
